import React, { useState } from 'react';
import { Select } from '../../../../page-builder/components/form-fields/Select';
import { FieldLabel } from '@measured/puck';
import CustomTooltip from '../../../../components/common/tooltip';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  fontSizeOptions,
  fontWeightOptions,
} from '../../../puck/reusable-props/font';
import { Props } from '../OrderDetails';
import ColorPicker from '../../../puck/reusable-props/ColorPicker';
import InputField from '../../../../components/common/input-field';

type CustomizeProps = {
  title: string;
  value: Props;
  onChange: (_value: Props) => void;
  isPlaceHolderNeed?: boolean;
};

const CustomizePanel = ({
  title,
  value,
  onChange,
  isPlaceHolderNeed = true,
}: CustomizeProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleChange = (key: string, option: string) => {
    onChange({
      ...value,
      [key]: option,
    });
  };

  return (
    <div>
      <div
        className="flex justify-between font-bold cursor-pointer py-2 bg-slate-100 rounded-md pagebuilder-theme"
        onClick={() => setIsOpen(!isOpen)}>
        <h3>Customize {title}</h3>
        {!isOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
      </div>
      {isOpen && (
        <div className="flex flex-col gap-4 mt-3 m-auto border border-gray-300 rounded py-2 px-4">
          {isPlaceHolderNeed && (
            <InputField
              value={value?.placeholder}
              onChange={(val: string) => {
                onChange({
                  ...value,
                  placeholder: val,
                });
              }}
              label={
                (
                  <CustomTooltip title="placeholder">
                    <span>Placeholder</span>
                  </CustomTooltip>
                ) as React.ReactNode as string
              }
            />
          )}
          <ColorPicker
            value={value?.textColor}
            onChange={color =>
              onChange({
                ...value,
                textColor: color,
              })
            }
            label={'Text Color'}
            tooltip={'textColor'}
          />
          <FieldLabel
            label={
              (
                <CustomTooltip title="fontSize">
                  <span>Font Size</span>
                </CustomTooltip>
              ) as React.ReactNode as string
            }>
            <Select
              className="pagebuilder-theme"
              value={value?.fontSize}
              onChange={e =>
                onChange({
                  ...value,
                  fontSize: e.target.value,
                })
              }
              options={fontSizeOptions}
              label="Size"
            />
          </FieldLabel>

          <FieldLabel
            label={
              (
                <CustomTooltip title="fontWeight">
                  <span>Font Weight</span>
                </CustomTooltip>
              ) as React.ReactNode as string
            }>
            <Select
              value={value?.fontWeight}
              onChange={e =>
                onChange({
                  ...value,
                  fontWeight: e.target.value,
                })
              }
              options={fontWeightOptions}
            />
          </FieldLabel>

          <FieldLabel
            label={
              (
                <CustomTooltip title="lineHeight">
                  <span>Padding Top</span>
                </CustomTooltip>
              ) as React.ReactNode as string
            }>
            <InputField
              value={value?.lineHeight || ''}
              onChange={e => {
                let newValue = e.replace(/[^0-9.]/g, '');

                if (newValue === '') {
                  newValue = '0';
                } else if (
                  isNaN(Number(newValue)) ||
                  parseFloat(newValue) < 1
                ) {
                  newValue = '';
                } else {
                  if ((newValue.match(/\./g) || []).length > 1) {
                    newValue = newValue.replace(/\.(?=.*\.)/g, '');
                  }
                }

                handleChange('lineHeight', newValue);
              }}
            />
          </FieldLabel>

          <FieldLabel
            label={
              (
                <CustomTooltip title="device">
                  <span>Device</span>
                </CustomTooltip>
              ) as React.ReactNode as string
            }>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                className="!appearance-auto"
                checked={value?.showOnDesktop}
                onChange={e =>
                  onChange({
                    ...value,
                    showOnDesktop: e.target.checked,
                  })
                }
              />
              <label>Show on desktop</label>
            </div>
            <div className="flex items-center space-x-2 mt-2">
              <input
                type="checkbox"
                className="!appearance-auto"
                checked={value?.showOnTablet}
                onChange={e =>
                  onChange({
                    ...value,
                    showOnTablet: e.target.checked,
                  })
                }
              />
              <label>Show on tablet</label>
            </div>
            <div className="flex items-center space-x-2 mt-2">
              <input
                type="checkbox"
                className="!appearance-auto"
                checked={value?.showOnMobile}
                onChange={e =>
                  onChange({
                    ...value,
                    showOnMobile: e.target.checked,
                  })
                }
              />
              <label>Show on mobile</label>
            </div>
          </FieldLabel>
        </div>
      )}
    </div>
  );
};

export default CustomizePanel;
